import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Grid,
  Group,
  Menu,
  Progress,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import {
  IconCircleCheck,
  IconCircleDashed,
  IconDots,
  IconEdit,
  IconExternalLink,
  IconLoader,
  IconMessage2,
  IconMessageQuestion,
  IconPlus,
  IconRefresh,
  IconSend,
  IconUserCheck,
  IconX,
} from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router';
import { CLIENT_REQUEST_TYPE_LABELS, type ChangeRequestDto, type ClarificationDto, type DossierRoundDto, type RequestDetailDto, type RequestViewDto, type WorkItemDto } from '@mexp/shared';
import { http } from '@/api/client';
import { PageHeader } from '@/components/PageHeader';
import { ErrorState, LoadingState } from '@/components/States';
import { FileList } from '@/features/files/FileList';
import { notificationKeys } from '@/layout/NotificationBell';
import { departmentName, useDepartments } from '@/features/shared/lookups';
import { formatBusinessDate, formatDate, formatDateTime, fromNow } from '@/lib/format';
import { useRequest } from './api';
import { BdStatusBadge, DueDate, RequestStatusBadge, TaskStatusBadge } from './components/badges';
import { RequestHistory } from './components/RequestHistory';
import { SampleTracking } from './components/SampleTracking';
import { ReasonModal } from './components/ReasonModal';
import {
  AddReferenceFilesModal,
  AddTasksModal,
  AskClientModal,
  BlockerList,
  ComplianceModal,
  EditRequestDrawer,
  ReviewerModal,
  SubmitDossierModal,
} from './components/RequestModals';
import { TaskDrawer } from './components/TaskDrawer';
import { useDrAction } from './mutations';

type ModalName = 'send' | 'reviewer' | 'tasks' | 'ask' | 'refs' | 'edit' | 'cancel' | null;

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
      <Text fz="sm" c="dimmed" className="nowrap">
        {label}
      </Text>
      <Text fz="sm" ta="right" style={{ minWidth: 0 }}>
        {children || '—'}
      </Text>
    </Group>
  );
}

function ChecklistCoverage({ view }: { view: RequestViewDto }) {
  const docs = view.request.requiredDocs;
  const type = view.request.requestType;
  if (!docs.length) {
    return (
      <Text fz="sm" c="dimmed">
        {type === 'dossier'
          ? 'The client asked for a dossier checklist, not specific documents.'
          : type === 'guidelines'
            ? 'The client asked for the guidelines, not specific documents.'
            : 'No checklist for this request.'}
      </Text>
    );
  }
  return (
    <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg" verticalSpacing="sm">
      {docs.map((d) => {
        const tasks = view.workItems.filter((i) => i.requiredDocKey === d.key && i.status !== 'cancelled');
        const done = tasks.length > 0 && tasks.every((t) => t.status === 'verified');
        const started = tasks.length > 0;
        return (
          <Group key={d.key} gap="sm" wrap="nowrap" align="flex-start">
            <ThemeIcon size={22} radius="xl" variant="light" color={done ? 'teal' : started ? 'blue' : 'gray'}>
              {done ? <IconCircleCheck size={14} /> : started ? <IconLoader size={14} /> : <IconCircleDashed size={14} />}
            </ThemeIcon>
            <Stack gap={0} style={{ minWidth: 0 }}>
              <Group gap={6}>
                <Text fz="sm" fw={500}>
                  {d.name}
                </Text>
                {!d.required && (
                  <Badge size="xs" variant="outline" color="gray">
                    Optional
                  </Badge>
                )}
              </Group>
              <Text fz="xs" c="dimmed">
                {done ? 'Verified' : started ? tasks.map((t) => `${t.assignee.name}: ${t.status}`).join(', ') : 'Not assigned yet'}
              </Text>
              {d.notes && (
                <Text fz="xs" c="dimmed" fs="italic">
                  {d.notes}
                </Text>
              )}
            </Stack>
          </Group>
        );
      })}
    </SimpleGrid>
  );
}

/** What the request needs, first thing on the page: the checklist and how far each document has got. */
function DocumentsNeededCard({ view }: { view: RequestViewDto }) {
  const { request } = view;
  const docs = request.requiredDocs;
  const done = docs.filter((d) => {
    const tasks = view.workItems.filter((i) => i.requiredDocKey === d.key && i.status !== 'cancelled');
    return tasks.length > 0 && tasks.every((t) => t.status === 'verified');
  }).length;
  return (
    <Card>
      <Stack gap="sm">
        <Group justify="space-between" wrap="wrap" gap="xs">
          <Group gap="sm" wrap="wrap">
            <Text fw={600}>Documents needed</Text>
            {request.origin === 'client' && request.requestType && (
              <Badge variant="light" color="teal" tt="none" size="md">
                Client asked for: {CLIENT_REQUEST_TYPE_LABELS[request.requestType]}
              </Badge>
            )}
          </Group>
          {docs.length > 0 && (
            <Text fz="sm" c="dimmed" className="tabular">
              {done} of {docs.length} verified
            </Text>
          )}
        </Group>
        <ChecklistCoverage view={view} />
      </Stack>
    </Card>
  );
}

function Tinted({ color, children }: { color: string; children: ReactNode }) {
  return (
    <Box p="sm" bg={`var(--mantine-color-${color}-light)`} style={{ borderRadius: 'var(--mantine-radius-md)' }}>
      <Stack gap={4}>{children}</Stack>
    </Box>
  );
}

/** One version of the dossier sent to BD and what BD decided about it, or one RA released straight to the client. */
function DossierRound({ round, request }: { round: DossierRoundDto; request: RequestDetailDto }) {
  const outcome = round.direct
    ? { label: round.release ? `Released as version ${round.release}` : 'Released', color: 'teal' }
    : round.outcome === 'approved'
      ? { label: round.release ? `Approved · released as version ${round.release}` : 'Approved', color: 'teal' }
      : round.outcome === 'returned'
        ? { label: 'Returned by BD', color: 'orange' }
        : { label: 'With BD for review', color: 'violet' };
  return (
    <Stack gap={8}>
      <Group gap="xs">
        <Text fz="sm" fw={600}>
          Round {round.round}
        </Text>
        <Badge size="sm" variant="light" color={outcome.color}>
          {outcome.label}
        </Badge>
      </Group>
      <Text fz="xs" c="dimmed">
        {round.direct ? 'Released to the client' : `Sent to ${request.bdReviewer?.name ?? 'BD'}`} by {round.sentBy?.name ?? 'RA'} · {formatDateTime(round.sentAt)}
      </Text>
      {round.notes && (
        <Text fz="sm" style={{ whiteSpace: 'pre-wrap' }}>
          {round.notes}
        </Text>
      )}
      <FileList files={round.files} empty={round.link ? undefined : 'No files attached'} />
      {round.link && (
        <Anchor href={round.link.url} target="_blank" rel="noopener noreferrer" fz="sm">
          <Group gap={4}>
            <IconExternalLink size={14} />
            {round.link.label || round.link.url}
          </Group>
        </Anchor>
      )}
      {round.outcome === 'pending' && (
        <Text fz="sm" c="dimmed">
          Waiting for SC-NBD to approve or return it in BDS.
        </Text>
      )}
      {round.outcome === 'returned' && (
        <Tinted color="orange">
          <Text fz="xs" fw={600} c="var(--mantine-color-orange-light-color)">
            Returned by {round.decidedBy?.name ?? 'BD'} in BDS · {formatDateTime(round.decidedAt)}
          </Text>
          {round.comment && (
            <Text fz="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {round.comment}
            </Text>
          )}
          {round.returnFiles.length > 0 && <FileList files={round.returnFiles} />}
        </Tinted>
      )}
      {round.outcome === 'approved' && !round.direct && (
        <Text fz="xs" c="dimmed">
          Approved in BDS by {round.decidedBy?.name ?? 'BD'} · {formatDateTime(round.decidedAt)}
          {round.comment ? ` · ${round.comment}` : ''}
        </Text>
      )}
    </Stack>
  );
}

/** One client query: what they asked, the tasks RA assigned for it, and RA's compliance once sent. */
function QueryEntry({
  query,
  tasks,
  blockers,
  canManage,
  canAsk,
  onOpenTask,
  onAssign,
  onAsk,
  onComply,
}: {
  query: ChangeRequestDto;
  tasks: WorkItemDto[];
  /** Coordinators only; undefined for anyone else. */
  blockers: string[] | undefined;
  canManage: boolean;
  canAsk: boolean;
  onOpenTask: (id: string) => void;
  onAssign: () => void;
  onAsk: () => void;
  onComply: () => void;
}) {
  const status = query.open
    ? { label: 'Open', color: 'grape' }
    : query.compliance
      ? { label: 'Compliance sent', color: 'teal' }
      : { label: `Answered by version ${query.resolvedVersion}`, color: 'teal' };
  return (
    <Stack gap={8}>
      <Group gap="xs" justify="space-between">
        <Group gap="xs">
          <Text fz="sm" fw={600}>
            Query {query.number}
          </Text>
          <Badge size="sm" variant="light" color={status.color}>
            {status.label}
          </Badge>
        </Group>
        <Text fz="xs" c="dimmed">
          {query.requestedBy.name} (client) · {formatDateTime(query.requestedAt)}
        </Text>
      </Group>
      <Text fz="sm" style={{ whiteSpace: 'pre-wrap' }}>
        {query.comment}
      </Text>
      {query.documents.length > 0 && (
        <Group gap={6}>
          {query.documents.map((d) => (
            <Badge key={d} size="sm" variant="outline" color="grape" tt="none">
              {d}
            </Badge>
          ))}
        </Group>
      )}
      {query.files.length > 0 && <FileList files={query.files} />}
      {tasks.length > 0 && (
        <Stack gap={2}>
          <Text fz="xs" c="dimmed">
            Tasks for this query
          </Text>
          {tasks.map((t) => (
            <UnstyledButton key={t.id} onClick={() => onOpenTask(t.id)} py={4}>
              <Group justify="space-between" wrap="nowrap" gap="sm">
                <Text fz="sm" truncate="end">
                  {t.title}{' '}
                  <Text span fz="sm" c="dimmed">
                    · {t.assignee.name}
                  </Text>
                </Text>
                <TaskStatusBadge status={t.status} />
              </Group>
            </UnstyledButton>
          ))}
        </Stack>
      )}
      {query.compliance && (
        <Tinted color="teal">
          <Text fz="xs" fw={600} c="var(--mantine-color-teal-light-color)">
            Compliance sent by {query.compliance.sentBy.name} · {formatDateTime(query.compliance.sentAt)}
          </Text>
          <Text fz="sm" style={{ whiteSpace: 'pre-wrap' }}>
            {query.compliance.comment}
          </Text>
          {query.compliance.files.length > 0 && <FileList files={query.compliance.files} />}
        </Tinted>
      )}
      {query.open && blockers && (
        <Group gap="xs">
          {canAsk && (
            <Button size="xs" variant="default" leftSection={<IconMessageQuestion size={14} />} onClick={onAsk}>
              Ask the client
            </Button>
          )}
          {canManage && (
            <Button size="xs" variant="default" leftSection={<IconPlus size={14} />} onClick={onAssign}>
              Assign tasks
            </Button>
          )}
          <Tooltip label={<BlockerList blockers={blockers} />} disabled={!blockers.length} multiline w={280} withinPortal>
            <span>
              <Button size="xs" color="teal" leftSection={<IconSend size={14} />} disabled={blockers.length > 0} onClick={onComply}>
                Send compliance
              </Button>
            </span>
          </Tooltip>
        </Group>
      )}
    </Stack>
  );
}

/**
 * The client's queries on the released dossier, newest first. Each is answered by its own compliance; the dossier stays
 * the one RA released.
 */
function QueriesCard({
  view,
  onOpenTask,
  onAssign,
  onAsk,
  onComply,
}: {
  view: RequestViewDto;
  onOpenTask: (id: string) => void;
  onAssign: (queryId: string) => void;
  onAsk: (queryId: string) => void;
  onComply: (query: ChangeRequestDto) => void;
}) {
  const { request, workItems, permissions } = view;
  if (!request.changeRequests.length) return null;
  const open = request.changeRequests.filter((c) => c.open).length;
  return (
    <Card>
      <Stack gap="md">
        <Group gap="sm" justify="space-between">
          <Group gap="sm">
            <ThemeIcon variant="light" color="grape" size="md">
              <IconMessage2 size={16} />
            </ThemeIcon>
            <Title order={4}>Client queries</Title>
          </Group>
          <Text fz="sm" c="dimmed">
            {open ? `${open} open` : 'All answered'}
          </Text>
        </Group>
        {[...request.changeRequests].reverse().map((q, i) => (
          <Fragment key={q.id}>
            {i > 0 && <Divider />}
            <QueryEntry
              query={q}
              tasks={workItems.filter((t) => t.queryId === q.id && t.status !== 'cancelled')}
              blockers={permissions.complianceBlockers[q.id]}
              canManage={permissions.canManageItems}
              canAsk={permissions.canAskClient}
              onOpenTask={onOpenTask}
              onAssign={() => onAssign(q.id)}
              onAsk={() => onAsk(q.id)}
              onComply={() => onComply(q)}
            />
          </Fragment>
        ))}
      </Stack>
    </Card>
  );
}

/** What RA asked the client to clarify (the requisition or one of their queries) and the client's answers, newest first. */
function ClarificationsCard({ request }: { request: RequestDetailDto }) {
  if (!request.clarifications.length) return null;
  const open = request.clarifications.filter((c) => c.open).length;
  const about = (c: ClarificationDto) => {
    const query = c.queryId ? request.changeRequests.find((q) => q.id === c.queryId) : null;
    return query ? `About Query ${query.number}` : 'About the requisition';
  };
  return (
    <Card>
      <Stack gap="md">
        <Group gap="sm" justify="space-between">
          <Group gap="sm">
            <ThemeIcon variant="light" color="orange" size="md">
              <IconMessageQuestion size={16} />
            </ThemeIcon>
            <Title order={4}>Clarifications</Title>
          </Group>
          <Text fz="sm" c="dimmed">
            {open ? `${open} waiting for the client` : 'All answered'}
          </Text>
        </Group>
        {[...request.clarifications].reverse().map((c, i) => (
          <Fragment key={c.id}>
            {i > 0 && <Divider />}
            <Stack gap={8}>
              <Group gap="xs" justify="space-between">
                <Group gap="xs">
                  <Text fz="sm" fw={600}>
                    Clarification {c.number}
                  </Text>
                  <Badge size="sm" variant="light" color={c.open ? 'orange' : 'teal'}>
                    {c.open ? 'Waiting for the client' : 'Answered'}
                  </Badge>
                  <Text fz="xs" c="dimmed">
                    {about(c)}
                  </Text>
                </Group>
                <Text fz="xs" c="dimmed">
                  {c.askedBy.name} · {formatDateTime(c.askedAt)}
                </Text>
              </Group>
              <Text fz="sm" style={{ whiteSpace: 'pre-wrap' }}>
                {c.question}
              </Text>
              {c.files.length > 0 && <FileList files={c.files} />}
              {c.answer && (
                <Tinted color="teal">
                  <Text fz="xs" fw={600} c="var(--mantine-color-teal-light-color)">
                    {c.answer.answeredBy.name} (client) answered · {formatDateTime(c.answer.answeredAt)}
                  </Text>
                  <Text fz="sm" style={{ whiteSpace: 'pre-wrap' }}>
                    {c.answer.comment}
                  </Text>
                  {c.answer.files.length > 0 && <FileList files={c.answer.files} />}
                </Tinted>
              )}
            </Stack>
          </Fragment>
        ))}
      </Stack>
    </Card>
  );
}

/** Every version of the dossier: what RA sent and what BD decided, newest first. */
function DossierVersionsCard({ request }: { request: RequestDetailDto }) {
  if (!request.dossierRounds.length) return null;
  const entries = request.dossierRounds
    .map((round) => ({ at: round.sentAt ?? '', key: `r${round.round}`, node: <DossierRound round={round} request={request} /> }))
    .sort((a, b) => b.at.localeCompare(a.at));
  return (
    <Card>
      <Stack gap="md">
        <Group gap="sm" justify="space-between">
          <Group gap="sm">
            <Title order={4}>Dossier versions</Title>
            {request.status === 'in_progress' && (request.bdStatus === 'pending_review' || request.bdStatus === 'returned') && <BdStatusBadge status={request.bdStatus} />}
          </Group>
          {request.releases.length > 0 && (
            <Text fz="sm" c="dimmed">
              {request.releases.length} released
            </Text>
          )}
        </Group>
        {entries.map((e, i) => (
          <Fragment key={e.key}>
            {i > 0 && <Divider />}
            {e.node}
          </Fragment>
        ))}
      </Stack>
    </Card>
  );
}

/** Whether BDS has the latest state of this request. */
function BdsSyncStatus({ request, onRetry, retrying }: { request: RequestDetailDto; onRetry: () => void; retrying: boolean }) {
  const { state, lastSyncedAt, lastError } = request.bds.sync;
  if (state === 'off') return <Text span fz="sm" c="dimmed">Not linked yet</Text>;
  const retry = (
    <Button size="compact-xs" variant="subtle" leftSection={<IconRefresh size={12} />} loading={retrying} onClick={onRetry}>
      Retry
    </Button>
  );
  if (state === 'synced') {
    return (
      <Tooltip label={`Last sent ${formatDateTime(lastSyncedAt)}`}>
        <Badge color="teal" variant="light">
          Up to date
        </Badge>
      </Tooltip>
    );
  }
  return (
    <Group gap={4} justify="flex-end" wrap="nowrap">
      <Tooltip label={lastError ?? 'Waiting to be sent'} multiline w={320} disabled={!lastError}>
        <Badge color={state === 'failed' ? 'red' : 'yellow'} variant="light">
          {state === 'failed' ? 'Not delivered' : lastError ? 'Retrying' : 'Sending'}
        </Badge>
      </Tooltip>
      {(state === 'failed' || lastError) && retry}
    </Group>
  );
}

function TasksTable({ items, queries, onOpen }: { items: WorkItemDto[]; queries: ChangeRequestDto[]; onOpen: (id: string) => void }) {
  const departments = useDepartments();
  const queryNumber = (id: string | null) => queries.find((q) => q.id === id)?.number;
  return (
    <div className="table-scroll">
      <Table miw={720}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Task</Table.Th>
            <Table.Th>Person</Table.Th>
            <Table.Th>Target</Table.Th>
            <Table.Th>Status</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {items.map((i) => (
            <Table.Tr key={i.id} className="clickable-row" onClick={() => onOpen(i.id)} opacity={i.status === 'cancelled' ? 0.55 : 1}>
              <Table.Td>
                <Text fz="sm" fw={600} td={i.status === 'cancelled' ? 'line-through' : undefined}>
                  {i.title}
                </Text>
                <Text fz="xs" c="dimmed">
                  <Text span ff="monospace" fz="xs">
                    {i.code}
                  </Text>{' '}
                  · {departmentName(departments.data, i.department)}
                  {i.queryId && (
                    <Badge ml={6} size="xs" variant="light" color="grape">
                      Query {queryNumber(i.queryId)}
                    </Badge>
                  )}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text fz="sm">{i.assignee.name}</Text>
              </Table.Td>
              <Table.Td>
                <Stack gap={2}>
                  <DueDate date={i.targetDate} open={i.status === 'assigned' || i.status === 'returned'} compact />
                  {i.targetProposal && (
                    <Badge size="xs" color="yellow" variant="light">
                      Date proposed
                    </Badge>
                  )}
                </Stack>
              </Table.Td>
              <Table.Td>
                <Group gap={4}>
                  <TaskStatusBadge status={i.status} />
                  {i.returnCount > 0 && i.status !== 'cancelled' && (
                    <Tooltip label={`Returned ${i.returnCount} time${i.returnCount === 1 ? '' : 's'}`}>
                      <Badge size="xs" variant="outline" color="orange">
                        ×{i.returnCount}
                      </Badge>
                    </Tooltip>
                  )}
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </div>
  );
}

export function RequestDetailPage() {
  const { id = '' } = useParams();
  const query = useRequest(id);
  const queryClient = useQueryClient();
  // Opening the request is seeing its updates: its highlight on the list and its notifications clear.
  useEffect(() => {
    if (!id) return;
    void http
      .post(`/dossier-requests/${id}/seen`)
      .then(() => Promise.all([queryClient.invalidateQueries({ queryKey: notificationKeys.all }), queryClient.invalidateQueries({ queryKey: ['dr', 'list'] })]))
      .catch(() => undefined);
  }, [id, queryClient]);
  const [modal, setModal] = useState<ModalName>(null);
  const [openItemId, setOpenItemId] = useState<string | null>(null);
  // The client query a task or a clarification is for (null: the requisition itself).
  const [taskQuery, setTaskQuery] = useState<string | null>(null);
  const [complyWith, setComplyWith] = useState<ChangeRequestDto | null>(null);
  const close = () => setModal(null);

  const cancel = useDrAction((r: { reason: string }) => http.post(`/dossier-requests/${id}/cancel`, r), 'Request cancelled', close);
  const retrySync = useDrAction(() => http.post(`/dossier-requests/${id}/bds-sync`), 'Sending to BDS again');

  if (query.isLoading) return <LoadingState />;
  if (query.isError || !query.data) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  const view = query.data;
  const { request, permissions, workItems } = view;
  const active = workItems.filter((i) => i.status !== 'cancelled');
  const removed = workItems.filter((i) => i.status === 'cancelled');
  const openItem = workItems.find((i) => i.id === openItemId) ?? null;
  const { total, verified, submitted, returned } = request.stats;
  const hasMenu = permissions.canEditDetails || permissions.canAssignReviewer || permissions.canCancel;
  const reviewer = request.bdReviewer;
  // Client-portal requisitions need no BD review: RA releases them to the client; BDS only shows them.
  const direct = request.origin === 'client';
  // A client-portal dossier is released once; after that the client raises queries and RA answers each with compliance.
  const released = direct && request.releases.length > 0;
  const openQueries = request.changeRequests.filter((c) => c.open).length;

  return (
    <>
      <PageHeader
        crumbs={[{ label: 'Document requests', to: '/internal/requests' }, { label: request.code }]}
        title={
          <>
            {request.productName} <Text span c="dimmed" fw={400} inherit>for {request.country}</Text>
          </>
        }
        badge={
          <Group gap={6}>
            {released && request.status === 'in_progress' ? (
              <Badge size="lg" variant="light" color="grape">
                {openQueries === 1 ? 'Query open' : `${openQueries} queries open`}
              </Badge>
            ) : request.status === 'in_progress' && !direct ? (
              <BdStatusBadge status={request.bdStatus} size="lg" />
            ) : (
              <RequestStatusBadge status={request.status} size="lg" />
            )}
            {request.status !== 'cancelled' && request.clarifications.some((c) => c.open) && (
              <Badge size="lg" variant="light" color="orange">
                Waiting for the client
              </Badge>
            )}
          </Group>
        }
        description={
          <>
            <Text span ff="monospace" fz="sm">
              {request.code}
            </Text>{' '}
            · {request.exporterName}
            {request.origin === 'bds' && (
              <Badge ml="xs" size="sm" variant="light" color="violet">
                Raised in BDS
              </Badge>
            )}
            {request.origin === 'client' && (
              <Badge ml="xs" size="sm" variant="light" color="teal">
                Client portal
              </Badge>
            )}
          </>
        }
        actions={
          <>
            {permissions.canAskClient && (
              <Button
                variant="default"
                leftSection={<IconMessageQuestion size={16} />}
                onClick={() => {
                  setTaskQuery(null);
                  setModal('ask');
                }}
              >
                Ask the client
              </Button>
            )}
            {request.status === 'in_progress' && !released && permissions.sendToBdBlockers.length + (permissions.canSendToBd ? 1 : 0) > 0 && (
              <Tooltip
                label={<BlockerList blockers={permissions.sendToBdBlockers} />}
                disabled={permissions.canSendToBd}
                multiline
                w={280}
                withinPortal
              >
                <span>
                  <Button color={direct ? 'teal' : 'violet'} leftSection={<IconSend size={16} />} disabled={!permissions.canSendToBd} onClick={() => setModal('send')}>
                    {direct ? 'Release to client' : request.bdStatus === 'returned' ? 'Resend to BDS' : 'Send to BDS'}
                  </Button>
                </span>
              </Tooltip>
            )}
            {hasMenu && (
              <Menu position="bottom-end" withinPortal>
                <Menu.Target>
                  <Button variant="default" rightSection={<IconDots size={16} />}>
                    More
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  {permissions.canEditDetails && (
                    <Menu.Item leftSection={<IconEdit size={16} />} onClick={() => setModal('edit')}>
                      Edit details and checklist
                    </Menu.Item>
                  )}
                  {permissions.canAssignReviewer && (
                    <Menu.Item leftSection={<IconUserCheck size={16} />} onClick={() => setModal('reviewer')}>
                      {direct ? (reviewer ? 'Change BD contact in BDS' : 'Choose BD contact in BDS') : reviewer ? 'Change SC-NBD reviewer' : 'Choose SC-NBD reviewer'}
                    </Menu.Item>
                  )}
                  {permissions.canCancel && (
                    <>
                      <Menu.Divider />
                      <Menu.Item color="red" leftSection={<IconX size={16} />} onClick={() => setModal('cancel')}>
                        Cancel request
                      </Menu.Item>
                    </>
                  )}
                </Menu.Dropdown>
              </Menu>
            )}
          </>
        }
      />

      {request.status === 'cancelled' && (
        <Alert color="gray" variant="light" mb="lg" title={`Cancelled ${formatDate(request.cancelledAt)}`}>
          {request.cancelReason}
        </Alert>
      )}
      {request.status === 'in_progress' && total > 0 && verified === total && request.bdStatus !== 'pending_review' && permissions.canSendToBd && (
        <Alert color="teal" variant="light" mb="lg" icon={<IconCircleCheck size={18} />} title="All documents verified">
          {direct
            ? 'Compile the dossier and release it to the client. They can download it as soon as you do; no BD review is needed.'
            : `Compile the dossier and send it to ${reviewer?.name ?? 'BD'} in BDS.`}
        </Alert>
      )}

      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, lg: 8 }}>
          <Stack gap="lg">
            <QueriesCard
              view={view}
              onOpenTask={setOpenItemId}
              onAssign={(queryId) => {
                setTaskQuery(queryId);
                setModal('tasks');
              }}
              onAsk={(queryId) => {
                setTaskQuery(queryId);
                setModal('ask');
              }}
              onComply={setComplyWith}
            />
            <ClarificationsCard request={request} />
            <DocumentsNeededCard view={view} />
            <Card>
              <Group justify="space-between" mb="sm">
                <Text fw={600}>Progress</Text>
                <Text fz="sm" c="dimmed" className="tabular">
                  {verified} of {total} verified
                </Text>
              </Group>
              {total > 0 ? (
                <Progress.Root size={12}>
                  <Progress.Section value={(verified / total) * 100} color="teal">
                    <Progress.Label>{verified || ''}</Progress.Label>
                  </Progress.Section>
                  <Progress.Section value={(submitted / total) * 100} color="yellow">
                    <Progress.Label>{submitted || ''}</Progress.Label>
                  </Progress.Section>
                  <Progress.Section value={(returned / total) * 100} color="orange">
                    <Progress.Label>{returned || ''}</Progress.Label>
                  </Progress.Section>
                </Progress.Root>
              ) : (
                <Text fz="sm" c="dimmed">
                  No tasks assigned yet.
                </Text>
              )}
              <Group gap="lg" mt="sm">
                {[
                  ['teal', 'Verified'],
                  ['yellow', 'Awaiting verification'],
                  ['orange', 'Returned'],
                  ['gray', 'To do'],
                ].map(([color, label]) => (
                  <Group key={label} gap={6}>
                    <div style={{ width: 10, height: 10, borderRadius: 3, background: `var(--mantine-color-${color}-${color === 'gray' ? 3 : 6})` }} />
                    <Text fz="xs" c="dimmed">
                      {label}
                    </Text>
                  </Group>
                ))}
              </Group>
            </Card>

            <DossierVersionsCard request={request} />

            <Card p={0}>
              <Group gap={8} px="md" pt="md">
                <Text fw={600}>Tasks</Text>
                <Badge size="sm" variant="light">
                  {active.length}
                </Badge>
              </Group>
              <Box>
                  <Group justify="space-between" p="md">
                    <Text fz="sm" c="dimmed">
                      Each task is one document from one person. Open a task to verify or return it.
                    </Text>
                    {permissions.canManageItems && (
                      <Button
                        size="xs"
                        leftSection={<IconPlus size={14} />}
                        onClick={() => {
                          setTaskQuery(null);
                          setModal('tasks');
                        }}
                      >
                        Assign tasks
                      </Button>
                    )}
                  </Group>
                  {active.length ? (
                    <TasksTable items={active} queries={request.changeRequests} onOpen={setOpenItemId} />
                  ) : (
                    <Text c="dimmed" fz="sm" px="md" pb="md">
                      {permissions.canManageItems ? 'Assign the first task to get documents moving.' : 'RA has not assigned any tasks yet.'}
                    </Text>
                  )}
                  {removed.length > 0 && (
                    <Stack gap={0} pt="md">
                      <Text fz="xs" c="dimmed" px="md" pb={4}>
                        Removed tasks
                      </Text>
                      <TasksTable items={removed} queries={request.changeRequests} onOpen={setOpenItemId} />
                    </Stack>
                  )}
              </Box>
            </Card>
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 4 }}>
          <Stack gap="lg">
            <Card>
              <Stack gap="sm">
                <Text fw={600}>Details</Text>
                <Detail label="Exporter">{request.exporterName}</Detail>
                <Detail label="Country">{request.country}</Detail>
                {request.genericName ? (
                  <>
                    <Detail label="Generic name">{request.genericName}</Detail>
                    <Detail label="Brand name">{request.brandName}</Detail>
                  </>
                ) : (
                  <Detail label="Brand / Generic name">{request.productName}</Detail>
                )}
                <Detail label="Pack size">{request.packSize}</Detail>
                {request.similarProduct && <Detail label="Existing similar product">{request.similarProduct}</Detail>}
                <Detail label="Category">{request.category}</Detail>
                <Detail label="Dosage form">{request.dosageForm}</Detail>
                <Detail label={request.origin === 'client' ? 'Date given to client' : 'Needed by'}>
                  {request.requiredBy ? (
                    formatBusinessDate(request.requiredBy)
                  ) : request.origin === 'client' ? (
                    <Text span c="orange" fz="sm">
                      Not set yet
                    </Text>
                  ) : null}
                </Detail>
                <Detail label="Raised by">
                  {request.createdBy.name}
                  {request.origin === 'bds' ? ' in BDS' : request.origin === 'client' ? ' (client portal)' : ''} · {formatDate(request.createdAt)}
                </Detail>
                {request.bds.lead?.name && <Detail label="BDS lead">{request.bds.lead.name}</Detail>}
                <Detail label={direct ? 'BD contact (BDS)' : 'SC-NBD reviewer'}>
                  {reviewer ? (
                    `${reviewer.name}${reviewer.teamName ? ` · ${reviewer.teamName}` : ''}`
                  ) : direct ? (
                    <Text span c="dimmed" fz="sm">
                      None · every SC-NBD user sees it
                    </Text>
                  ) : (
                    <Text span c="orange" fz="sm">
                      Not chosen
                    </Text>
                  )}
                </Detail>
                <Group justify="space-between" wrap="nowrap" gap="md">
                  <Text fz="sm" c="dimmed" className="nowrap">
                    BDS
                  </Text>
                  <BdsSyncStatus request={request} onRetry={() => retrySync.mutate()} retrying={retrySync.isPending} />
                </Group>
                {request.notes && (
                  <Text fz="sm" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}>
                    {request.notes}
                  </Text>
                )}
              </Stack>
            </Card>
            <SampleTracking requestId={request.id} cancelled={request.status === 'cancelled'} />
            <Card>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text fw={600}>Reference files</Text>
                  {permissions.canEditDetails && (
                    <Button size="compact-xs" variant="subtle" leftSection={<IconPlus size={12} />} onClick={() => setModal('refs')}>
                      Add
                    </Button>
                  )}
                </Group>
                <FileList files={request.referenceFiles} empty="None" />
              </Stack>
            </Card>
            <RequestHistory requestId={request.id} />
          </Stack>
        </Grid.Col>
      </Grid>

      <TaskDrawer item={openItem} request={request} canManage={permissions.canManageItems} onClose={() => setOpenItemId(null)} />
      <SubmitDossierModal request={request} opened={modal === 'send'} onClose={close} />
      <ReviewerModal request={request} opened={modal === 'reviewer'} onClose={close} />
      <AddTasksModal request={request} queryId={taskQuery} opened={modal === 'tasks'} onClose={close} />
      <ComplianceModal request={request} query={complyWith} onClose={() => setComplyWith(null)} />
      <AskClientModal request={request} queryId={taskQuery} opened={modal === 'ask'} onClose={close} />
      <AddReferenceFilesModal request={request} opened={modal === 'refs'} onClose={close} />
      <EditRequestDrawer request={request} opened={modal === 'edit'} onClose={close} />
      <ReasonModal
        opened={modal === 'cancel'}
        onClose={close}
        title={`Cancel ${request.code}`}
        description="Open tasks are withdrawn and everyone involved is notified. This cannot be undone."
        confirmLabel="Cancel request"
        confirmColor="red"
        loading={cancel.isPending}
        onSubmit={(r) => cancel.mutate({ reason: r.reason })}
      />
    </>
  );
}
